// Intelligent Health Data Hook - Based on working useRealTimeHealthData patterns
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './useAuth';
import iomtApiService from '../services/iomtApiService';
import { apiConfig } from '../config/apiConfig';
// Import the new Health Data Integration layer
import HealthDataIntegration from '../utils/HealthDataIntegration';

const useIntelligentHealthData = (days = 7) => {
  const { user, userInfo } = useAuth();
  const isFetchingRef = useRef(false);
  
  // Empty data structure to prevent null errors - matching useRealTimeHealthData
  const EMPTY_HEALTH_DATA = useMemo(() => ({
    vitals: {
      heartRate: null,
      bloodOxygen: null,
      bloodPressure: null,
      temperature: null,
      respiratoryRate: null,
      bloodGlucose: null
    },
    activity: {
      steps: { value: 0, source: null, timestamp: null },
      calories: { value: 0, source: null, timestamp: null },
      distance: { value: 0, unit: 'km' },
      activeMinutes: 0
    },
    wellness: {
      sleep: null,
      hydration: { glasses: 0, ml: 0, target: 8, percentage: 0 },
      stress: null,
      nutrition: null
    },
    score: {
      overall: 0,
      status: 'No Data',
      components: {
        activity: 0,
        vitals: 0,
        sleep: 0,
        nutrition: 0,
        mental: 0
      },
      dataCompleteness: 0
    },
    realTimeMetrics: {
      heartRate: null,
      steps: 0,
      calories: 0,
      water: 0,
      sleep: null,
      glucose: null
    },
    devices: [],
    lastUpdated: new Date().toISOString(),
    dataQuality: {
      percentage: 0,
      level: 'No Data',
      availableMetrics: 0,
      totalMetrics: 8
    },
    alerts: [],
    recommendations: [],
    medications: [],
    activePlans: []
  }), []);

  const [healthData, setHealthData] = useState(EMPTY_HEALTH_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState({});
  const [lastSync, setLastSync] = useState(null);
  const [dataAvailability, setDataAvailability] = useState({});
  
  // State for profile data from Azure Data Lake - matching useRealTimeHealthData
  const [profileDataFromLake, setProfileDataFromLake] = useState(null);
  const [activePlansFromLake, setActivePlansFromLake] = useState([]);

  // Fetch profile data from Azure Data Lake - same as useRealTimeHealthData
  const fetchProfileDataFromLake = useCallback(async () => {
    const userId = user?.localAccountId || userInfo?.localAccountId;
    
    if (!userId) {
      return null;
    }

    try {
      // Check cache first
      const cacheKey = `medicureon_profile_${userId}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheExpiry = 1 * 60 * 1000; // 1 minute cache
      
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        if (Date.now() - parsed.timestamp < cacheExpiry) {
          setProfileDataFromLake(parsed.medicationsData);
          setActivePlansFromLake(parsed.activePlansData);
          return { medicationsData: parsed.medicationsData, activePlansData: parsed.activePlansData };
        }
      }
      
      // Construct URLs - same as useRealTimeHealthData
      const profileUrl = `${apiConfig.backendUrl}/api/profiles/${userId}?code=${apiConfig.functionKeys.profileGet}`;
      const plansUrl = `${apiConfig.backendUrl}/api/profiles/${userId}/active-plans?code=${apiConfig.functionKeys.getActivePlans || apiConfig.functionKeys.profileGet}`;
      
      // Fetch fresh data - parallel requests
      const [profilePromise, plansPromise] = await Promise.allSettled([
        fetch(profileUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }),
        fetch(plansUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })
      ]);
      
      let medicationsData = null;
      let activePlansData = [];
      
      // Process profile response - same logic as useRealTimeHealthData
      if (profilePromise.status === 'fulfilled' && profilePromise.value.ok) {
        const result = await profilePromise.value.json();
        
        if (result.success && result.data) {
          console.log('💊 PROFILE FETCH: Success, processing data structure');
          console.log('💊 PROFILE FETCH: result.data keys:', Object.keys(result.data));
          
          // NEW: Extract from correct location - data.lifestyle
          if (result.data.lifestyle) {
            medicationsData = result.data.lifestyle;
            console.log('💊 PROFILE FETCH: Found lifestyle data:', Object.keys(medicationsData));
          } else {
            // Fallback to old structure
            medicationsData = result.data.lifestyle || 
                            result.data['medications-lifestyle'] || 
                            result.data.medicationsLifestyle || 
                            null;
          }
          
          // NEW: Extract activePlans from correct location - data.activePlans  
          if (result.data.activePlans) {
            console.log('💊 PROFILE FETCH: Found activePlans in data.activePlans:', result.data.activePlans);
            // The activePlans is an object with indices, convert to array
            if (typeof result.data.activePlans === 'object' && !Array.isArray(result.data.activePlans)) {
              activePlansData = Object.values(result.data.activePlans);
            } else {
              activePlansData = result.data.activePlans;
            }
          } else if (result.data['active-plans']) {
            activePlansData = result.data['active-plans'];
          }
          
          console.log('💊 PROFILE FETCH: Final medicationsData:', medicationsData);
          console.log('💊 PROFILE FETCH: Final activePlansData:', activePlansData);
        }
      }
      
      // Process active plans response - same logic as useRealTimeHealthData
      if (plansPromise.status === 'fulfilled' && plansPromise.value.ok) {
        const plansResult = await plansPromise.value.json();
        
        if (plansResult.data) {
          if (Array.isArray(plansResult.data)) {
            activePlansData = plansResult.data;
          } else if (plansResult.activePlans) {
            activePlansData = plansResult.activePlans;
          }
        }
      }
      
      setProfileDataFromLake(medicationsData);
      setActivePlansFromLake(activePlansData);
      
      // Cache the data
      sessionStorage.setItem(cacheKey, JSON.stringify({
        medicationsData,
        activePlansData,
        timestamp: Date.now()
      }));
      
      return { medicationsData, activePlansData };
    } catch (error) {
      return null;
    }
  }, [user, userInfo]);

  // Get manual water intake data from EarnPointsTab
  const getManualWaterIntake = useCallback(() => {
    try {
      const userId = user?.localAccountId || userInfo?.localAccountId;
      if (!userId) return 0;

      // Check for hydration data in localStorage (from EarnPointsTab)
      const hydrationKey = `medicureon_daily_hydration_${userId}`;
      const hydrationData = localStorage.getItem(hydrationKey);
      
      if (hydrationData) {
        const parsed = JSON.parse(hydrationData);
        const today = new Date().toDateString();
        if (parsed.date === today) {
          return parsed.totalMl || 0;
        }
      }

      // Also check sessionStorage for recent entries
      const sessionKey = `hydration_entry_${userId}`;
      const sessionData = sessionStorage.getItem(sessionKey);
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        return parsed.totalMl || 0;
      }

      return 0;
    } catch (error) {
      return 0;
    }
  }, [user, userInfo]);

  // Extract medications from profile data - fixed for correct data structure
  const extractMedicationsFromProfile = (currentProfileData) => {
    let allMedications = [];
    
    console.log('💊 EXTRACT MEDICATIONS: Input data:', currentProfileData);
    
    if (currentProfileData) {
      // Match the same paths as useRealTimeHealthData for consistency
      // Try different possible locations for medications data
      const lifestyleData = currentProfileData.data?.lifestyle || 
                           currentProfileData.data?.['medications-lifestyle'] || 
                           currentProfileData.data?.medicationsLifestyle ||
                           currentProfileData.lifestyle || 
                           currentProfileData['medications-lifestyle'] || 
                           currentProfileData.medicationsLifestyle || 
                           {};
                           
      const currentMedications = lifestyleData.currentMedications || currentProfileData.currentMedications || [];
      const supplements = lifestyleData.supplements?.vitaminsSupplements || currentProfileData.supplements?.vitaminsSupplements || [];
      const herbalRemedies = lifestyleData.supplements?.herbalRemedies || currentProfileData.supplements?.herbalRemedies || [];
      
      console.log('💊 EXTRACT: lifestyleData found:', lifestyleData ? 'Available' : 'UNDEFINED');
      console.log('💊 EXTRACT: Medications found - Prescriptions:', currentMedications.length, 'Supplements:', supplements.length, 'Herbal:', herbalRemedies.length);
      
      // Format prescription medications
      if (Array.isArray(currentMedications)) {
        allMedications.push(...currentMedications.map(med => ({
          name: typeof med === 'string' ? med : (med.name || 'Unknown Medication'),
          dosage: typeof med === 'string' ? 'As prescribed' : (med.dosage || 'As prescribed'),
          frequency: typeof med === 'string' ? 'As directed' : (med.frequency || 'As directed'),
          type: 'prescription',
          prescribingDoctor: typeof med === 'string' ? '' : (med.prescribingDoctor || ''),
          purpose: typeof med === 'string' ? '' : (med.purpose || ''),
          time: typeof med === 'string' ? 'As directed' : 
                (med.frequency === 'Once daily' ? '9:00 AM' : 
                 med.frequency === 'Twice daily' ? '8:00 AM, 8:00 PM' : 
                 'As directed'),
          taken: false
        })));
      }
      
      // Format supplements
      if (Array.isArray(supplements)) {
        allMedications.push(...supplements
          .filter(supp => supp && supp.trim() !== '' && supp.trim() !== ',')
          .map(supp => ({
            name: supp.replace(/,$/, '').trim(),
            dosage: 'As directed',
            frequency: 'Daily',
            type: 'supplement',
            time: '9:00 AM',
            taken: false
          })));
      }
      
      // Format herbal remedies
      if (Array.isArray(herbalRemedies)) {
        allMedications.push(...herbalRemedies
          .filter(herb => herb && herb.trim() !== '' && herb.trim() !== ',')
          .map(herb => ({
            name: herb.replace(/,$/, '').trim(),
            dosage: 'As directed',
            frequency: 'As needed',
            type: 'herbal',
            time: 'As needed',
            taken: false
          })));
      }
    }
    
    return allMedications;
  };

  // Transform Azure Data - simplified version of useRealTimeHealthData logic
  const transformAzureData = useCallback((azureResponse, profileData = null, activePlans = null) => {
    const currentProfileData = profileData || profileDataFromLake;
    const currentActivePlans = activePlans || activePlansFromLake;
    
    console.log('💊 PROFILE DATA STATUS:');
    console.log('  - External profileData:', profileData ? 'Available' : 'UNDEFINED');
    console.log('  - Internal profileDataFromLake:', profileDataFromLake ? 'Available' : 'UNDEFINED');
    console.log('  - Final currentProfileData:', currentProfileData ? 'Available' : 'UNDEFINED');
    if (!currentProfileData) {
      console.log('  ❌ CRITICAL: No profile data available for medication extraction!');
    }
    
    // Get medications from profile
    const medications = extractMedicationsFromProfile(currentProfileData);
    
    console.log('💊 MEDICATION EXTRACTION RESULT:');
    console.log('  - Input profile data type:', typeof currentProfileData);
    console.log('  - Profile data keys:', currentProfileData ? Object.keys(currentProfileData) : 'null');
    console.log('  - Extracted medications count:', medications ? medications.length : 0);
    console.log('  - Extracted medications:', medications);
    
    if (!azureResponse?.data?.length) {
      console.log('💊 EARLY RETURN: No Azure data, returning with', medications?.length || 0, 'medications');
      return { ...EMPTY_HEALTH_DATA, medications, activePlans: currentActivePlans || [] };
    }

    // Separate files by device type for smart selection with comprehensive device support
    const filesByDevice = {};
    console.log('🔍 DEVICE DETECTION: Processing', azureResponse.data.length, 'files');
    
    azureResponse.data.forEach(record => {
      const fileName = record._metadata?.fileName || '';
      console.log('📱 File detected:', fileName);
      
      // Comprehensive device detection for global market including iPhone-specific data
      const deviceType = 
        fileName.includes('oura-ring') ? 'oura-ring' :
        fileName.includes('apple-watch') ? 'apple-watch' :
        fileName.includes('iphone') || fileName.includes('ios-health') ? 'iphone' :
        fileName.includes('withings-body-scan') || fileName.includes('withings-bpm-connect') || fileName.includes('withings-uscan-nutrio') ? 'withings' :
        fileName.includes('dexcom') ? 'dexcom' :
        fileName.includes('fitbit') ? 'fitbit' :
        fileName.includes('garmin') ? 'garmin' :
        fileName.includes('samsung-health') ? 'samsung' :
        fileName.includes('withings-watch') ? 'withings-watch' :
        fileName.includes('polar') ? 'polar' :
        fileName.includes('amazfit') ? 'amazfit' :
        fileName.includes('huawei') ? 'huawei' :
        'unknown';
      
      if (!filesByDevice[deviceType]) {
        filesByDevice[deviceType] = [];
      }
      filesByDevice[deviceType].push(record);
    });

    // Get latest file from each device type with enhanced sorting
    const getLatestFile = (files) => {
      if (!files || files.length === 0) return null;
      
      const filesByDate = {};
      files.forEach(record => {
        const fileName = record._metadata?.fileName || '';
        const dateMatch = fileName.match(/(\d{8})/);
        
        if (dateMatch) {
          const dateKey = dateMatch[1];
          if (!filesByDate[dateKey]) {
            filesByDate[dateKey] = [];
          }
          filesByDate[dateKey].push(record);
        }
      });
      
      const sortedDates = Object.keys(filesByDate).sort((a, b) => b.localeCompare(a));
      const latestDate = sortedDates[0];
      const latestFiles = filesByDate[latestDate] || [];
      
      return latestFiles.sort((a, b) => {
        const timeA = a._metadata?.fileName?.match(/_(\d{4})-/)?.[1] || '0000';
        const timeB = b._metadata?.fileName?.match(/_(\d{4})-/)?.[1] || '0000';
        return timeB.localeCompare(timeA);
      })[0] || files[0];
    };

    // Get latest files by device with comprehensive device support including iPhone
    const ouraFile = getLatestFile(filesByDevice['oura-ring']);
    const appleWatchFile = getLatestFile(filesByDevice['apple-watch']);
    const iphoneFile = getLatestFile(filesByDevice['iphone']) || getLatestFile(filesByDevice['iphone-healthkit']);
    const withingsFile = getLatestFile(filesByDevice['withings']) || getLatestFile(filesByDevice['withings-body-scan']);
    const withingsBPMFile = getLatestFile(filesByDevice['withings-bpm-connect']);
    const withingsUScanFile = getLatestFile(filesByDevice['withings-uscan-nutrio']);
    const dexcomFile = getLatestFile(filesByDevice['dexcom']);
    const fitbitFile = getLatestFile(filesByDevice['fitbit']);
    const garminFile = getLatestFile(filesByDevice['garmin']);
    const samsungFile = getLatestFile(filesByDevice['samsung']);
    const withingsWatchFile = getLatestFile(filesByDevice['withings-watch']);
    const polarFile = getLatestFile(filesByDevice['polar']);
    const amazfitFile = getLatestFile(filesByDevice['amazfit']);
    const huaweiFile = getLatestFile(filesByDevice['huawei']);

    // Medical-Grade Sleep Data Priority System
    // Priority: Oura Ring (specialist) > Apple Watch > Withings > Fitbit > Garmin > Samsung > Others
    let sleepSource = null;
    let sleepHours = null;
    let sleepEfficiency = null;
    let deepSleep = null;
    let remSleep = null;
    let restingHeartRate = null;
    let heartRateVariability = null;

    // Oura Ring - Sleep specialist device (Priority 1)
    if (ouraFile?.healthMetrics?.sleep?.duration) {
      // Oura Ring typically provides sleep in minutes, convert to hours
      const ouraSleepValue = ouraFile.healthMetrics.sleep.duration.value;
      sleepHours = ouraSleepValue && ouraSleepValue > 24 ? (ouraSleepValue / 60) : ouraSleepValue;
      sleepEfficiency = ouraFile.healthMetrics.sleep.efficiency?.value;
      deepSleep = ouraFile.healthMetrics.sleep.deepSleep?.value;
      remSleep = ouraFile.healthMetrics.sleep.remSleep?.value;
      restingHeartRate = ouraFile.healthMetrics.recovery?.restingHeartRate?.value;
      heartRateVariability = ouraFile.healthMetrics.hrv?.rmssd?.value;
      sleepSource = 'Oura Ring';
      
      console.log('💤 SLEEP DEBUG: Oura Ring raw value:', ouraSleepValue, 'converted to hours:', sleepHours);
    }
    // Apple Watch - Comprehensive sleep tracking (Priority 2) 
    else if (appleWatchFile?.healthMetrics?.sleep) {
      sleepHours = appleWatchFile.healthMetrics.sleep.duration ? (appleWatchFile.healthMetrics.sleep.duration / 60) : null;
      sleepEfficiency = appleWatchFile.healthMetrics.sleep.efficiency;
      deepSleep = appleWatchFile.healthMetrics.sleep.stages?.deep;
      remSleep = appleWatchFile.healthMetrics.sleep.stages?.rem;
      restingHeartRate = appleWatchFile.healthMetrics.vitalSigns?.heartRate?.resting;
      heartRateVariability = appleWatchFile.healthMetrics.vitalSigns?.heartRateVariability?.rmssd;
      sleepSource = 'Apple Watch';
    }
    // Withings Sleep/Watch - Medical validation (Priority 3)
    else if (withingsFile?.healthMetrics?.sleep?.sleep || withingsWatchFile?.healthMetrics?.sleep?.sleep) {
      const sleepFile = withingsFile?.healthMetrics?.sleep?.sleep ? withingsFile : withingsWatchFile;
      sleepHours = sleepFile.healthMetrics.sleep.sleep.totalSleep;
      sleepEfficiency = sleepFile.healthMetrics.sleep.sleep.sleepEfficiency;
      deepSleep = sleepFile.healthMetrics.sleep.sleep.deepSleep;
      remSleep = sleepFile.healthMetrics.sleep.sleep.remSleep;
      restingHeartRate = sleepFile.healthMetrics.sleep.sleep.restingHeartRate;
      heartRateVariability = sleepFile.healthMetrics.sleep.sleep.heartRateVariability;
      sleepSource = sleepFile === withingsFile ? 'Withings Sleep' : 'Withings Watch';
    }
    // Fitbit - Activity tracker with sleep (Priority 4)
    else if (fitbitFile?.healthMetrics?.sleep?.sleep) {
      sleepHours = fitbitFile.healthMetrics.sleep.sleep.totalSleep;
      sleepEfficiency = fitbitFile.healthMetrics.sleep.sleep.sleepEfficiency;
      deepSleep = fitbitFile.healthMetrics.sleep.sleep.deepSleep;
      remSleep = fitbitFile.healthMetrics.sleep.sleep.remSleep;
      restingHeartRate = fitbitFile.healthMetrics.sleep.sleep.restingHeartRate;
      heartRateVariability = fitbitFile.healthMetrics.sleep.sleep.heartRateVariability;
      sleepSource = 'Fitbit';
    }
    // Garmin - Sports/fitness tracking (Priority 5)
    else if (garminFile?.healthMetrics?.sleep?.sleep) {
      sleepHours = garminFile.healthMetrics.sleep.sleep.totalSleep;
      sleepEfficiency = garminFile.healthMetrics.sleep.sleep.sleepEfficiency;
      deepSleep = garminFile.healthMetrics.sleep.sleep.deepSleep;
      remSleep = garminFile.healthMetrics.sleep.sleep.remSleep;
      restingHeartRate = garminFile.healthMetrics.sleep.sleep.restingHeartRate;
      heartRateVariability = garminFile.healthMetrics.sleep.sleep.heartRateVariability;
      sleepSource = 'Garmin';
    }
    // Samsung Health - Android ecosystem (Priority 6)
    else if (samsungFile?.healthMetrics?.sleep?.sleep) {
      sleepHours = samsungFile.healthMetrics.sleep.sleep.totalSleep;
      sleepEfficiency = samsungFile.healthMetrics.sleep.sleep.sleepEfficiency;
      deepSleep = samsungFile.healthMetrics.sleep.sleep.deepSleep;
      remSleep = samsungFile.healthMetrics.sleep.sleep.remSleep;
      restingHeartRate = samsungFile.healthMetrics.sleep.sleep.restingHeartRate;
      heartRateVariability = samsungFile.healthMetrics.sleep.sleep.heartRateVariability;
      sleepSource = 'Samsung Health';
    }
    // Other devices fallback (Polar, Amazfit, Huawei)
    else {
      const fallbackFiles = [polarFile, amazfitFile, huaweiFile].filter(Boolean);
      for (const file of fallbackFiles) {
        if (file?.healthMetrics?.sleep?.sleep) {
          sleepHours = file.healthMetrics.sleep.sleep.totalSleep;
          sleepEfficiency = file.healthMetrics.sleep.sleep.sleepEfficiency;
          deepSleep = file.healthMetrics.sleep.sleep.deepSleep;
          remSleep = file.healthMetrics.sleep.sleep.remSleep;
          restingHeartRate = file.healthMetrics.sleep.sleep.restingHeartRate;
          heartRateVariability = file.healthMetrics.sleep.sleep.heartRateVariability;
          sleepSource = file === polarFile ? 'Polar' : file === amazfitFile ? 'Amazfit' : 'Huawei';
          break;
        }
      }
    }

    // ✅ DEVICE-AGNOSTIC HEART RATE SELECTION: Latest timestamp wins, regardless of brand
    // Removes vendor-specific priority system in favor of most recent data
    let heartRateSource = null;
    let heartRate = null;
    
    // Collect all heart rate sources with timestamps for device-agnostic selection
    const allHeartRateSources = [];
    
    // Add all available heart rate readings with timestamps
    if (ouraFile?.healthMetrics?.sleep?.restingHeartRate) {
      allHeartRateSources.push({
        value: ouraFile.healthMetrics.sleep.restingHeartRate,
        timestamp: new Date(ouraFile.timestamp).getTime(),
        source: 'Oura Ring (Sleep)',
        file: ouraFile
      });
    }
    
    if (ouraFile?.healthMetrics?.vitalSigns?.heartRate?.value) {
      allHeartRateSources.push({
        value: ouraFile.healthMetrics.vitalSigns.heartRate.value,
        timestamp: new Date(ouraFile.timestamp).getTime(),
        source: 'Oura Ring',
        file: ouraFile
      });
    }
    
    if (ouraFile?.healthMetrics?.sleep?.sleep?.restingHeartRate) {
      allHeartRateSources.push({
        value: ouraFile.healthMetrics.sleep.sleep.restingHeartRate,
        timestamp: new Date(ouraFile.timestamp).getTime(),
        source: 'Oura Ring (Sleep)',
        file: ouraFile
      });
    }
    
    if (withingsFile?.healthMetrics?.cardiovascular?.ecgData?.heartRate) {
      allHeartRateSources.push({
        value: withingsFile.healthMetrics.cardiovascular.ecgData.heartRate,
        timestamp: new Date(withingsFile.timestamp).getTime(),
        source: 'Withings ECG',
        file: withingsFile
      });
    }
    
    if (withingsFile?.healthMetrics?.vitalSigns?.heartRate?.value) {
      allHeartRateSources.push({
        value: withingsFile.healthMetrics.vitalSigns.heartRate.value,
        timestamp: new Date(withingsFile.timestamp).getTime(),
        source: 'Withings BPM',
        file: withingsFile
      });
    }
    
    if (appleWatchFile?.healthMetrics?.vitalSigns?.heartRate?.current) {
      allHeartRateSources.push({
        value: appleWatchFile.healthMetrics.vitalSigns.heartRate.current,
        timestamp: new Date(appleWatchFile.timestamp).getTime(),
        source: 'Apple Watch',
        file: appleWatchFile
      });
    }
    
    if (iphoneFile?.healthMetrics?.vitalSigns?.heartRate?.value) {
      allHeartRateSources.push({
        value: iphoneFile.healthMetrics.vitalSigns.heartRate.value,
        timestamp: new Date(iphoneFile.timestamp).getTime(),
        source: 'iPhone',
        file: iphoneFile
      });
    }
    
    if (fitbitFile?.healthMetrics?.vitalSigns?.heartRate?.value) {
      allHeartRateSources.push({
        value: fitbitFile.healthMetrics.vitalSigns.heartRate.value,
        timestamp: new Date(fitbitFile.timestamp).getTime(),
        source: 'Fitbit',
        file: fitbitFile
      });
    }
    
    if (garminFile?.healthMetrics?.vitalSigns?.heartRate?.value) {
      allHeartRateSources.push({
        value: garminFile.healthMetrics.vitalSigns.heartRate.value,
        timestamp: new Date(garminFile.timestamp).getTime(),
        source: 'Garmin',
        file: garminFile
      });
    }
    
    if (samsungFile?.healthMetrics?.vitalSigns?.heartRate?.value) {
      allHeartRateSources.push({
        value: samsungFile.healthMetrics.vitalSigns.heartRate.value,
        timestamp: new Date(samsungFile.timestamp).getTime(),
        source: 'Samsung Health',
        file: samsungFile
      });
    }
    
    if (withingsWatchFile?.healthMetrics?.vitalSigns?.heartRate?.value) {
      allHeartRateSources.push({
        value: withingsWatchFile.healthMetrics.vitalSigns.heartRate.value,
        timestamp: new Date(withingsWatchFile.timestamp).getTime(),
        source: 'Withings Watch',
        file: withingsWatchFile
      });
    }
    
    // Select latest heart rate reading regardless of device brand
    let latestHeartRateTimestamp = 0;
    let latestHeartRateData = null;
    
    allHeartRateSources.forEach(hrSource => {
      if (hrSource.timestamp > latestHeartRateTimestamp) {
        latestHeartRateTimestamp = hrSource.timestamp;
        latestHeartRateData = hrSource;
      }
    });
    
    if (latestHeartRateData) {
      heartRate = latestHeartRateData.value;
      heartRateSource = latestHeartRateData.source;
      console.log(`🔄 DEVICE-AGNOSTIC HR: Selected ${heartRateSource} (${heartRate} bpm) from ${new Date(latestHeartRateTimestamp).toISOString()}`);
    }
    
    // Healthcare Institution Multi-Context Heart Rate System (for backward compatibility)
    const heartRateContexts = {
      resting: heartRate,
      active: null,
      sleep: restingHeartRate || heartRate,
      primary: heartRate,
      primarySource: heartRateSource
    };

    // Activity Tracking Priority System
    // ✅ DEVICE-AGNOSTIC PRIORITY: Latest timestamp wins, regardless of brand
    let activitySource = null;
    let steps = 0;
    let calories = 0;
    let activeMinutes = 0;
    let distanceKm = '0.0';
    let latestActivityTimestamp = 0;

    // Collect all available activity data with timestamps
    const allActivitySources = [
      { file: iphoneFile, name: 'iPhone', priority: 'iOS Native Phone' },
      { file: appleWatchFile, name: 'Apple Watch', priority: 'iOS Native Watch' },
      { file: garminFile, name: 'Garmin', priority: 'Fitness Specialist' },
      { file: fitbitFile, name: 'Fitbit', priority: 'Popular Tracker' },
      { file: samsungFile, name: 'Samsung Health', priority: 'Android Native' },
      { file: withingsWatchFile, name: 'Withings Watch', priority: 'Medical Grade' },
      { file: ouraFile, name: 'Oura Ring', priority: 'Sleep & Recovery' },
      { file: polarFile, name: 'Polar', priority: 'Heart Rate Specialist' },
      { file: amazfitFile, name: 'Amazfit', priority: 'Budget Tracker' },
      { file: huaweiFile, name: 'Huawei', priority: 'Regional Leader' }
    ];

    // Find the most recent activity data across all devices
    for (const { file, name, priority } of allActivitySources) {
      if (file?.healthMetrics?.activity) {
        // Get timestamp for this device's data
        const deviceTimestamp = new Date(file.timestamp || file.syncTime || 0).getTime();
        
        // Handle different data structures: Oura Ring uses direct numbers, others use .value
        const hasSteps = (file.healthMetrics.activity.steps?.value > 0) || 
                        (typeof file.healthMetrics.activity.steps === 'number' && file.healthMetrics.activity.steps > 0);
        
        // Use this device if it has more recent data or this is the first device with steps
        if (hasSteps && (deviceTimestamp > latestActivityTimestamp || latestActivityTimestamp === 0)) {
          // Extract data handling both .value structure and direct number structure (Oura Ring, Apple Watch)
          steps = file.healthMetrics.activity.steps?.value || file.healthMetrics.activity.steps || 0;
          calories = file.healthMetrics.activity.activeCalories?.value || 
                     file.healthMetrics.activity.activeCalories ||
                     file.healthMetrics.activity.caloriesBurned?.value ||
                     file.healthMetrics.activity.calories || 0;
          activeMinutes = file.healthMetrics.activity.activeMinutes?.value || 
                          Math.round(steps / 100);
          
          const distanceMeters = file.healthMetrics.activity.distance?.value || 0;
          distanceKm = distanceMeters > 0 ? (distanceMeters / 1000).toFixed(1) : 
                       ((steps * 0.762) / 1000).toFixed(1);
          
          activitySource = name;
          latestActivityTimestamp = deviceTimestamp;
          
          console.log(`🎯 Activity data selected from ${name} (${priority}):`, {
            steps,
            calories,
            timestamp: new Date(deviceTimestamp).toISOString(),
            reason: deviceTimestamp > 0 ? 'Most recent data' : 'First available data'
          });
        }
      }
    }

    // ✅ DEVICE-AGNOSTIC TEMPERATURE TRACKING: Latest timestamp wins, includes Oura Ring
    // Collect all temperature sources with timestamps for device-agnostic selection
    const allTemperatureSources = [];
    
    // Oura Ring - Sleep temperature (high accuracy)
    if (ouraFile?.healthMetrics?.sleep?.bodyTemperature) {
      allTemperatureSources.push({
        value: ouraFile.healthMetrics.sleep.bodyTemperature,
        timestamp: new Date(ouraFile.timestamp).getTime(),
        source: 'Oura Ring (Sleep)',
        file: ouraFile
      });
    }
    
    // Apple Watch - Body temperature sensor
    if (appleWatchFile?.healthMetrics?.vitalSigns?.bodyTemperature?.wrist) {
      allTemperatureSources.push({
        value: appleWatchFile.healthMetrics.vitalSigns.bodyTemperature.wrist,
        timestamp: new Date(appleWatchFile.timestamp).getTime(),
        source: 'Apple Watch',
        file: appleWatchFile
      });
    }
    
    // Withings - Body temperature from scales/devices
    if (withingsFile?.healthMetrics?.vitalSigns?.bodyTemperature?.value) {
      allTemperatureSources.push({
        value: withingsFile.healthMetrics.vitalSigns.bodyTemperature.value,
        timestamp: new Date(withingsFile.timestamp).getTime(),
        source: 'Withings',
        file: withingsFile
      });
    }
    
    // Samsung Health - Temperature tracking
    if (samsungFile?.healthMetrics?.vitalSigns?.bodyTemperature?.value) {
      allTemperatureSources.push({
        value: samsungFile.healthMetrics.vitalSigns.bodyTemperature.value,
        timestamp: new Date(samsungFile.timestamp).getTime(),
        source: 'Samsung Health',
        file: samsungFile
      });
    }
    
    // Select latest temperature reading regardless of device brand
    let latestTemperatureTimestamp = 0;
    let temperature = null;
    let temperatureSource = null;
    
    allTemperatureSources.forEach(tempData => {
      if (tempData.timestamp > latestTemperatureTimestamp) {
        latestTemperatureTimestamp = tempData.timestamp;
        temperature = tempData.value;
        temperatureSource = tempData.source;
      }
    });
    
    if (temperatureSource) {
      console.log(`🔄 DEVICE-AGNOSTIC TEMP: Selected ${temperatureSource} (${temperature}°F) from ${new Date(latestTemperatureTimestamp).toISOString()}`);
    }

    // Blood Oxygen: Apple Watch Series 6+
    let bloodOxygen = null;
    if (appleWatchFile?.healthMetrics?.vitalSigns?.bloodOxygen?.current) {
      bloodOxygen = appleWatchFile.healthMetrics.vitalSigns.bloodOxygen.current;
    }

    // ✅ DEVICE-AGNOSTIC WATER/HYDRATION TRACKING: Latest timestamp wins + manual entry
    // Collect all hydration sources with timestamps for device-agnostic selection
    const allHydrationSources = [];
    
    // Add device hydration data with timestamps
    if (appleWatchFile?.healthMetrics?.hydration?.glasses_of_water) {
      allHydrationSources.push({
        value: appleWatchFile.healthMetrics.hydration.glasses_of_water * 250, // Convert glasses to ML
        timestamp: new Date(appleWatchFile.timestamp).getTime(),
        source: 'Apple Watch',
        file: appleWatchFile
      });
    }
    
    // Alternative Apple Watch hydration extraction paths
    if (appleWatchFile?.healthMetrics?.wellness?.hydration?.value) {
      allHydrationSources.push({
        value: appleWatchFile.healthMetrics.wellness.hydration.value,
        timestamp: new Date(appleWatchFile.timestamp).getTime(),
        source: 'Apple Watch (Wellness)',
        file: appleWatchFile
      });
    }
    
    if (iphoneFile?.healthMetrics?.wellness?.hydration?.value) {
      allHydrationSources.push({
        value: iphoneFile.healthMetrics.wellness.hydration.value,
        timestamp: new Date(iphoneFile.timestamp).getTime(),
        source: 'iPhone Health',
        file: iphoneFile
      });
    }
    
    if (samsungFile?.healthMetrics?.wellness?.hydration?.value) {
      allHydrationSources.push({
        value: samsungFile.healthMetrics.wellness.hydration.value,
        timestamp: new Date(samsungFile.timestamp).getTime(),
        source: 'Samsung Health',
        file: samsungFile
      });
    }
    
    if (fitbitFile?.healthMetrics?.wellness?.hydration?.value) {
      allHydrationSources.push({
        value: fitbitFile.healthMetrics.wellness.hydration.value,
        timestamp: new Date(fitbitFile.timestamp).getTime(),
        source: 'Fitbit',
        file: fitbitFile
      });
    }
    
    if (garminFile?.healthMetrics?.wellness?.hydration?.value) {
      allHydrationSources.push({
        value: garminFile.healthMetrics.wellness.hydration.value,
        timestamp: new Date(garminFile.timestamp).getTime(),
        source: 'Garmin',
        file: garminFile
      });
    }
    
    // Select latest hydration reading regardless of device brand
    let latestHydrationTimestamp = 0;
    let deviceWaterML = 0;
    let hydrationSource = null;
    
    allHydrationSources.forEach(hydrationData => {
      if (hydrationData.timestamp > latestHydrationTimestamp) {
        latestHydrationTimestamp = hydrationData.timestamp;
        deviceWaterML = hydrationData.value;
        hydrationSource = hydrationData.source;
      }
    });
    
    if (hydrationSource) {
      console.log(`🔄 DEVICE-AGNOSTIC HYDRATION: Selected ${hydrationSource} (${deviceWaterML}ml) from ${new Date(latestHydrationTimestamp).toISOString()}`);
    }
    
    // Combine with manual entry (user input always takes priority if higher)
    const manualWaterML = getManualWaterIntake();
    const waterML = Math.max(deviceWaterML, manualWaterML);
    
    // Log final hydration source
    const finalHydrationSource = manualWaterML > deviceWaterML ? 'Manual Entry' : (hydrationSource || 'No Device Data');
    console.log(`💧 FINAL HYDRATION: ${waterML}ml from ${finalHydrationSource}`);

    // Determine primary device for metadata
    const primaryDevice = appleWatchFile || ouraFile || withingsFile;
    const deviceType = activitySource || sleepSource || heartRateSource || 'Multi-Device';

    // Glucose Monitoring Priority System
    // Priority: Dexcom (medical CGM) > Withings UScan Nutrio > Fitbit (glucose tracking) > Samsung > Others
    let glucoseLevel = null;
    let glucoseTrend = null;
    let glucoseSource = null;

    // Dexcom - Medical-grade Continuous Glucose Monitor (Priority 1)
    if (dexcomFile?.healthMetrics?.glucose) {
      glucoseLevel = dexcomFile.healthMetrics.glucose.currentLevel?.value || 
                     dexcomFile.healthMetrics.glucose.level?.value;
      glucoseTrend = dexcomFile.healthMetrics.glucose.trend || 
                     dexcomFile.healthMetrics.glucose.direction;
      glucoseSource = 'Dexcom CGM';
    }
    // Withings UScan Nutrio - Advanced urinalysis (Priority 2)
    else if (withingsFile?.healthMetrics?.glucose) {
      glucoseLevel = withingsFile.healthMetrics.glucose.level?.value || 
                     withingsFile.healthMetrics.glucose.urineGlucose?.value;
      glucoseSource = 'Withings UScan Nutrio';
    }
    // Fitbit - Consumer glucose tracking (Priority 3)
    else if (fitbitFile?.healthMetrics?.glucose) {
      glucoseLevel = fitbitFile.healthMetrics.glucose.level?.value;
      glucoseTrend = fitbitFile.healthMetrics.glucose.trend;
      glucoseSource = 'Fitbit';
    }
    // Samsung Health - Android ecosystem (Priority 4)
    else if (samsungFile?.healthMetrics?.glucose) {
      glucoseLevel = samsungFile.healthMetrics.glucose.level?.value;
      glucoseSource = 'Samsung Health';
    }
    // Other devices fallback
    else {
      const glucoseFallbacks = [
        { file: appleWatchFile, name: 'Apple Watch' },
        { file: garminFile, name: 'Garmin' },
        { file: withingsWatchFile, name: 'Withings Watch' }
      ];
      
      for (const { file, name } of glucoseFallbacks) {
        if (file?.healthMetrics?.glucose) {
          glucoseLevel = file.healthMetrics.glucose.level?.value;
          glucoseTrend = file.healthMetrics.glucose.trend;
          glucoseSource = name;
          break;
        }
      }
    }

    // Legacy support for backward compatibility
    const bloodGlucose = glucoseLevel;
    
    // ✅ DEVICE-AGNOSTIC STRESS TRACKING: Latest timestamp wins, includes HRV-based stress
    // Collect all stress sources with timestamps for device-agnostic selection
    const allStressSources = [];
    
    // Oura Ring - HRV-based stress calculation (high accuracy for sleep/recovery stress)
    if (ouraFile?.healthMetrics?.sleep?.heartRateVariability) {
      // Convert HRV to stress level (inverse relationship: lower HRV = higher stress)
      const hrv = ouraFile.healthMetrics.sleep.heartRateVariability;
      const stressFromHRV = Math.max(0, Math.min(100, 100 - (hrv * 2))); // Simple HRV to stress mapping
      allStressSources.push({
        value: stressFromHRV,
        timestamp: new Date(ouraFile.timestamp).getTime(),
        source: 'Oura Ring (HRV)',
        file: ouraFile
      });
    }
    
    // Direct stress readings from devices
    if (ouraFile?.healthMetrics?.wellness?.stress?.value) {
      allStressSources.push({
        value: ouraFile.healthMetrics.wellness.stress.value,
        timestamp: new Date(ouraFile.timestamp).getTime(),
        source: 'Oura Ring',
        file: ouraFile
      });
    }
    
    if (appleWatchFile?.healthMetrics?.wellness?.stress?.value) {
      allStressSources.push({
        value: appleWatchFile.healthMetrics.wellness.stress.value,
        timestamp: new Date(appleWatchFile.timestamp).getTime(),
        source: 'Apple Watch',
        file: appleWatchFile
      });
    }
    
    // Withings - Nervous system health converted to stress scale
    if (withingsFile?.healthMetrics?.cardiovascular?.nervousSystemHealth?.score) {
      const nervousScore = withingsFile.healthMetrics.cardiovascular.nervousSystemHealth.score;
      const stressFromNS = Math.max(0, 100 - nervousScore); // Higher nervous system health = lower stress
      allStressSources.push({
        value: stressFromNS,
        timestamp: new Date(withingsFile.timestamp).getTime(),
        source: 'Withings (ANS)',
        file: withingsFile
      });
    }
    
    if (garminFile?.healthMetrics?.wellness?.stress?.value) {
      allStressSources.push({
        value: garminFile.healthMetrics.wellness.stress.value,
        timestamp: new Date(garminFile.timestamp).getTime(),
        source: 'Garmin',
        file: garminFile
      });
    }
    
    if (fitbitFile?.healthMetrics?.wellness?.stress?.value) {
      allStressSources.push({
        value: fitbitFile.healthMetrics.wellness.stress.value,
        timestamp: new Date(fitbitFile.timestamp).getTime(),
        source: 'Fitbit',
        file: fitbitFile
      });
    }
    
    if (samsungFile?.healthMetrics?.wellness?.stress?.value) {
      allStressSources.push({
        value: samsungFile.healthMetrics.wellness.stress.value,
        timestamp: new Date(samsungFile.timestamp).getTime(),
        source: 'Samsung Health',
        file: samsungFile
      });
    }
    
    // Select latest stress reading regardless of device brand
    let latestStressTimestamp = 0;
    let stressLevel = null;
    let stressSource = null;
    
    allStressSources.forEach(stressData => {
      if (stressData.timestamp > latestStressTimestamp) {
        latestStressTimestamp = stressData.timestamp;
        stressLevel = stressData.value;
        stressSource = stressData.source;
      }
    });
    
    if (stressSource) {
      console.log(`🔄 DEVICE-AGNOSTIC STRESS: Selected ${stressSource} (${stressLevel}%) from ${new Date(latestStressTimestamp).toISOString()}`);
    }

    // Body Composition Priority System
    // Priority: Withings Body Scan > Withings UScan Nutrio > Apple Watch > Samsung > Fitbit > Garmin > Others
    let bodyComposition = null;
    let bodyCompositionSource = null;

    // Withings Body Scan - Medical-grade body composition (Priority 1)
    if (withingsFile?.healthMetrics?.bodyComposition) {
      bodyComposition = {
        weight: withingsFile.healthMetrics.bodyComposition.weight,
        bmi: withingsFile.healthMetrics.bodyComposition.bmi,
        bodyFatPercentage: withingsFile.healthMetrics.bodyComposition.bodyFatPercentage,
        muscleMass: withingsFile.healthMetrics.bodyComposition.muscleMass,
        visceralFat: withingsFile.healthMetrics.bodyComposition.visceralFat,
        metabolicAge: withingsFile.healthMetrics.bodyComposition.metabolicAge,
        waterPercentage: withingsFile.healthMetrics.bodyComposition.waterPercentage,
        source: 'Withings Body Scan'
      };
      bodyCompositionSource = 'Withings Body Scan';
      console.log('⚖️ WEIGHT DEBUG: Found Withings body composition:', bodyComposition.weight);
    }
    // Apple Watch - Smart scale integration (Priority 2)
    else if (appleWatchFile?.healthMetrics?.bodyComposition) {
      bodyComposition = {
        weight: appleWatchFile.healthMetrics.bodyComposition.weight,
        bmi: appleWatchFile.healthMetrics.bodyComposition.bmi,
        bodyFatPercentage: appleWatchFile.healthMetrics.bodyComposition.bodyFatPercentage,
        muscleMass: appleWatchFile.healthMetrics.bodyComposition.muscleMass,
        source: 'Apple Watch'
      };
      bodyCompositionSource = 'Apple Watch';
    }
    // Samsung Health - Android ecosystem (Priority 3)
    else if (samsungFile?.healthMetrics?.bodyComposition) {
      bodyComposition = {
        weight: samsungFile.healthMetrics.bodyComposition.weight,
        bmi: samsungFile.healthMetrics.bodyComposition.bmi,
        bodyFatPercentage: samsungFile.healthMetrics.bodyComposition.bodyFatPercentage,
        muscleMass: samsungFile.healthMetrics.bodyComposition.muscleMass,
        source: 'Samsung Health'
      };
      bodyCompositionSource = 'Samsung Health';
    }
    // Fitbit - Popular platform (Priority 4)
    else if (fitbitFile?.healthMetrics?.bodyComposition) {
      bodyComposition = {
        weight: fitbitFile.healthMetrics.bodyComposition.weight,
        bmi: fitbitFile.healthMetrics.bodyComposition.bmi,
        bodyFatPercentage: fitbitFile.healthMetrics.bodyComposition.bodyFatPercentage,
        source: 'Fitbit'
      };
      bodyCompositionSource = 'Fitbit';
    }
    // Garmin - Sports focus (Priority 5)
    else if (garminFile?.healthMetrics?.bodyComposition) {
      bodyComposition = {
        weight: garminFile.healthMetrics.bodyComposition.weight,
        bmi: garminFile.healthMetrics.bodyComposition.bmi,
        bodyFatPercentage: garminFile.healthMetrics.bodyComposition.bodyFatPercentage,
        source: 'Garmin'
      };
      bodyCompositionSource = 'Garmin';
    }
    // Other devices fallback
    else {
      const bodyCompFallbacks = [
        { file: withingsWatchFile, name: 'Withings Watch' },
        { file: polarFile, name: 'Polar' },
        { file: amazfitFile, name: 'Amazfit' },
        { file: huaweiFile, name: 'Huawei' },
        { file: ouraFile, name: 'Oura Ring' }
      ];
      
      for (const { file, name } of bodyCompFallbacks) {
        if (file?.healthMetrics?.bodyComposition) {
          bodyComposition = {
            weight: file.healthMetrics.bodyComposition.weight,
            bmi: file.healthMetrics.bodyComposition.bmi,
            bodyFatPercentage: file.healthMetrics.bodyComposition.bodyFatPercentage,
            muscleMass: file.healthMetrics.bodyComposition.muscleMass,
            source: name
          };
          bodyCompositionSource = name;
          break;
        }
      }
    }

    // VO2 Max Priority System
    // Priority: Garmin (sports specialist) > Apple Watch > Fitbit > Polar > Samsung > Others
    let vo2Max = null;
    let vo2MaxSource = null;

    // Garmin - Sports and fitness specialist (Priority 1)
    if (garminFile?.healthMetrics?.activity?.vo2Max?.value) {
      vo2Max = garminFile.healthMetrics.activity.vo2Max.value;
      vo2MaxSource = 'Garmin';
    }
    // Apple Watch - Comprehensive fitness tracking (Priority 2)
    else if (appleWatchFile?.healthMetrics?.activity?.vo2Max?.value) {
      vo2Max = appleWatchFile.healthMetrics.activity.vo2Max.value;
      vo2MaxSource = 'Apple Watch';
    }
    // Fitbit - Popular fitness platform (Priority 3)
    else if (fitbitFile?.healthMetrics?.activity?.vo2Max?.value) {
      vo2Max = fitbitFile.healthMetrics.activity.vo2Max.value;
      vo2MaxSource = 'Fitbit';
    }
    // Polar - Sports monitoring specialist (Priority 4)
    else if (polarFile?.healthMetrics?.activity?.vo2Max?.value) {
      vo2Max = polarFile.healthMetrics.activity.vo2Max.value;
      vo2MaxSource = 'Polar';
    }
    // Samsung Health - Android ecosystem (Priority 5)
    else if (samsungFile?.healthMetrics?.activity?.vo2Max?.value) {
      vo2Max = samsungFile.healthMetrics.activity.vo2Max.value;
      vo2MaxSource = 'Samsung Health';
    }
    // Other devices fallback
    else {
      const vo2MaxFallbacks = [
        { file: withingsWatchFile, name: 'Withings Watch' },
        { file: amazfitFile, name: 'Amazfit' },
        { file: huaweiFile, name: 'Huawei' },
        { file: ouraFile, name: 'Oura Ring' }
      ];
      
      for (const { file, name } of vo2MaxFallbacks) {
        if (file?.healthMetrics?.activity?.vo2Max?.value) {
          vo2Max = file.healthMetrics.activity.vo2Max.value;
          vo2MaxSource = name;
          break;
        }
      }
    }
    
    // Nervous System & Vital Signs Priority System
    // Priority: Withings (medical-grade) > Apple Watch > Samsung > Garmin > Fitbit > Others
    let nervousSystemHealth = null;
    let nervousSystemSource = null;
    let respiratoryRate = null;
    let respiratoryRateSource = null;
    let bloodPressure = null;
    let bloodPressureSource = null;

    // Withings - Medical-grade vital signs monitoring (Priority 1)
    if (withingsFile?.healthMetrics?.cardiovascular?.nervousSystemHealth?.score) {
      nervousSystemHealth = withingsFile.healthMetrics.cardiovascular.nervousSystemHealth.score;
      nervousSystemSource = 'Withings Body Scan';
    }
    
    // Withings BPM Connect - Dedicated blood pressure device (Priority 1)
    if (withingsBPMFile?.healthMetrics?.vitalSigns?.bloodPressure) {
      bloodPressure = withingsBPMFile.healthMetrics.vitalSigns.bloodPressure;
      bloodPressureSource = 'Withings BPM Connect';
    }
    // Fallback to general Withings file for blood pressure
    if (!bloodPressure && withingsFile?.healthMetrics?.vitalSigns?.bloodPressure) {
      bloodPressure = withingsFile.healthMetrics.vitalSigns.bloodPressure;
      bloodPressureSource = 'Withings BPM';
    }

    // Apple Watch - Comprehensive vital signs (Priority 2)
    if (!respiratoryRate && appleWatchFile?.healthMetrics?.vitalSigns?.respiratoryRate?.value) {
      respiratoryRate = appleWatchFile.healthMetrics.vitalSigns.respiratoryRate.value;
      respiratoryRateSource = 'Apple Watch';
    }
    if (!bloodPressure && appleWatchFile?.healthMetrics?.vitalSigns?.bloodPressure) {
      bloodPressure = appleWatchFile.healthMetrics.vitalSigns.bloodPressure;
      bloodPressureSource = 'Apple Watch';
    }

    // Samsung Health - Android ecosystem vital signs (Priority 3)
    if (!respiratoryRate && samsungFile?.healthMetrics?.vitalSigns?.respiratoryRate?.value) {
      respiratoryRate = samsungFile.healthMetrics.vitalSigns.respiratoryRate.value;
      respiratoryRateSource = 'Samsung Health';
    }
    if (!bloodPressure && samsungFile?.healthMetrics?.vitalSigns?.bloodPressure) {
      bloodPressure = samsungFile.healthMetrics.vitalSigns.bloodPressure;
      bloodPressureSource = 'Samsung Health';
    }

    // Other devices fallback for vital signs
    const vitalSignsFallbacks = [
      { file: garminFile, name: 'Garmin' },
      { file: fitbitFile, name: 'Fitbit' },
      { file: withingsWatchFile, name: 'Withings Watch' },
      { file: polarFile, name: 'Polar' },
      { file: amazfitFile, name: 'Amazfit' },
      { file: huaweiFile, name: 'Huawei' }
    ];
    
    for (const { file, name } of vitalSignsFallbacks) {
      if (!respiratoryRate && file?.healthMetrics?.vitalSigns?.respiratoryRate?.value) {
        respiratoryRate = file.healthMetrics.vitalSigns.respiratoryRate.value;
        respiratoryRateSource = name;
      }
      if (!bloodPressure && file?.healthMetrics?.vitalSigns?.bloodPressure) {
        bloodPressure = file.healthMetrics.vitalSigns.bloodPressure;
        bloodPressureSource = name;
      }
      if (respiratoryRate && bloodPressure) break;
    }

    const primaryFileDate = primaryDevice?._metadata?.fileDate;

    // ✅ COMPREHENSIVE HEALTH SCORE CALCULATION (Not Hardcoded)
    // Use real data from multiple sources to calculate meaningful health score
    const activityScore = Math.min(100, Math.round((steps / 10000) * 100));
    const heartRateScore = heartRate ? (heartRate >= 60 && heartRate <= 100 ? 85 : 60) : 0;
    const sleepScore = sleepEfficiency ? sleepEfficiency : 0;
    const temperatureScore = temperature ? (temperature >= 97 && temperature <= 99.5 ? 90 : 70) : 0;
    const hydrationScore = waterML > 0 ? Math.min(100, (waterML / 2000) * 100) : 0;
    const stressScore = stressLevel !== null ? Math.max(0, 100 - stressLevel) : 0;
    
    // Calculate realistic component scores from actual device data
    let vitalSignsScore = 0;
    let vitalCount = 0;
    
    if (heartRate) { vitalSignsScore += heartRateScore; vitalCount++; }
    if (bloodOxygen) { 
      const oxygenScore = bloodOxygen >= 95 ? 90 : bloodOxygen >= 90 ? 70 : 50;
      vitalSignsScore += oxygenScore; 
      vitalCount++; 
    }
    if (temperature) { vitalSignsScore += temperatureScore; vitalCount++; }
    
    const finalVitalScore = vitalCount > 0 ? Math.round(vitalSignsScore / vitalCount) : 0;
    
    // Mental health score from stress and mindfulness
    let mentalScore = 0;
    if (stressLevel !== null) {
      mentalScore = Math.max(0, 100 - stressLevel); // Lower stress = higher mental score
    }
    
    // Nutrition score from hydration data
    let nutritionScore = 0;
    if (appleWatchFile?.healthMetrics?.hydration?.hydration_percentage) {
      nutritionScore = appleWatchFile.healthMetrics.hydration.hydration_percentage;
    } else if (waterML > 0) {
      nutritionScore = Math.min(100, (waterML / 2000) * 100);
    }
    
    // Use Apple Watch sleep score if available, otherwise calculate from efficiency
    let finalSleepScore = 0;
    if (appleWatchFile?.healthMetrics?.sleep?.sleep_score) {
      finalSleepScore = appleWatchFile.healthMetrics.sleep.sleep_score;
    } else if (sleepEfficiency) {
      finalSleepScore = sleepEfficiency;
    }
    
    console.log(`📊 Component Scores: Activity=${activityScore}%, Vitals=${finalVitalScore}%, Sleep=${finalSleepScore}%, Mental=${mentalScore}%, Nutrition=${nutritionScore}%`);
    
    // Check if we have vendor-specific comprehensive scores (most accurate)
    const ouraReadinessScore = ouraFile?.healthMetrics?.readiness?.score;
    const ouraActivityScore = ouraFile?.healthMetrics?.activity?.activityScore;
    const ouraSleepScore = ouraFile?.healthMetrics?.sleep?.sleepScore;
    
    // Check if we have Apple Watch business intelligence score
    const appleWatchScore = appleWatchFile?.businessIntelligence?.completeness;
    
    // Check for Fitbit/Garmin/Samsung comprehensive scores
    const fitbitScore = fitbitFile?.healthMetrics?.overallScore || fitbitFile?.businessIntelligence?.completeness;
    const garminScore = garminFile?.healthMetrics?.overallScore || garminFile?.businessIntelligence?.completeness;
    const samsungScore = samsungFile?.healthMetrics?.overallScore || samsungFile?.businessIntelligence?.completeness;
    const withingsScore = withingsFile?.healthMetrics?.overallScore || withingsFile?.businessIntelligence?.completeness;
    
    // Calculate comprehensive score with weighted components
    let overallScore = 0;
    let componentCount = 0;
    
    // Priority order: Use vendor's own comprehensive scoring first, then calculate
    if (appleWatchScore) {
      overallScore = appleWatchScore;
      console.log(`🍎 Health Score: Using Apple Watch Business Intelligence Score (${appleWatchScore}%)`);
    } else if (ouraReadinessScore) {
      overallScore = ouraReadinessScore;
      console.log(`🔬 Health Score: Using Oura Ring Readiness Score (${ouraReadinessScore}%)`);
    } else if (fitbitScore) {
      overallScore = fitbitScore;
      console.log(`📱 Health Score: Using Fitbit Overall Score (${fitbitScore}%)`);
    } else if (garminScore) {
      overallScore = garminScore;
      console.log(`⌚ Health Score: Using Garmin Overall Score (${garminScore}%)`);
    } else if (samsungScore) {
      overallScore = samsungScore;
      console.log(`� Health Score: Using Samsung Health Score (${samsungScore}%)`);
    } else if (withingsScore) {
      overallScore = withingsScore;
      console.log(`⚖️ Health Score: Using Withings Overall Score (${withingsScore}%)`);
    } else {
      // Calculate from available components
      if (activityScore > 0) { overallScore += activityScore * 0.3; componentCount += 0.3; }
      if (heartRateScore > 0) { overallScore += heartRateScore * 0.2; componentCount += 0.2; }
      if (sleepScore > 0) { overallScore += sleepScore * 0.25; componentCount += 0.25; }
      if (temperatureScore > 0) { overallScore += temperatureScore * 0.1; componentCount += 0.1; }
      if (hydrationScore > 0) { overallScore += hydrationScore * 0.05; componentCount += 0.05; }
      if (stressScore > 0) { overallScore += stressScore * 0.1; componentCount += 0.1; }
      
      // Normalize based on available components
      if (componentCount > 0) {
        overallScore = Math.round(overallScore / componentCount);
      } else {
        overallScore = 0; // No data available
      }
      
      console.log(`📊 Health Score: Calculated from ${Math.round(componentCount * 100)}% of components (${overallScore}%)`);
    }
    
    // Build result - same structure as useRealTimeHealthData
    const result = {
      vitals: {
        heartRate: heartRate ? { 
          value: heartRate, 
          unit: 'bpm', 
          status: heartRate >= 60 && heartRate <= 100 ? 'normal' : heartRate < 60 ? 'low' : 'high',
          source: heartRateSource,
          contexts: heartRateContexts // Healthcare-grade multi-context data
        } : null,
        bloodOxygen: bloodOxygen ? { value: bloodOxygen, unit: '%', status: bloodOxygen >= 95 ? 'normal' : 'low' } : null,
        bloodPressure: null,
        temperature: temperature ? { value: temperature, unit: '°F', status: 'normal', source: temperatureSource } : null,
        respiratoryRate: null,
        bloodGlucose: null
      },
      activity: {
        steps: { value: steps, source: activitySource, timestamp: primaryFileDate },
        calories: { value: calories, source: activitySource, timestamp: primaryFileDate },
        distance: { value: distanceKm, unit: 'km' },
        activeMinutes: activeMinutes
      },
      wellness: {
        sleep: sleepHours ? { 
          duration: sleepHours, 
          efficiency: sleepEfficiency,
          deepSleep: deepSleep,
          remSleep: remSleep,
          quality: sleepEfficiency >= 85 ? 'Good' : sleepEfficiency >= 70 ? 'Fair' : 'Poor', 
          source: sleepSource 
        } : null,
        hydration: { 
          glasses: Math.round(waterML / 250), 
          ml: waterML, 
          target: 8, 
          percentage: Math.round((waterML / 2000) * 100),
          source: deviceWaterML > 0 ? activitySource : 'Manual Entry'
        },
        stress: stressLevel !== null ? {
          level: stressLevel <= 25 ? 'Low' : stressLevel <= 50 ? 'Moderate' : stressLevel <= 75 ? 'High' : 'Very High',
          numeric: stressLevel,
          value: stressLevel,
          source: stressSource,
          unit: 'score'
        } : null,
        nutrition: null
      },
      score: {
        overall: overallScore,
        status: overallScore >= 80 ? 'Good' : overallScore >= 60 ? 'Fair' : 'Needs Attention',
        components: {
          activity: activityScore,
          vitals: finalVitalScore,
          sleep: finalSleepScore,
          nutrition: Math.round(nutritionScore),
          mental: Math.round(mentalScore)
        },
        dataCompleteness: appleWatchScore || ouraReadinessScore || fitbitScore || garminScore || samsungScore || withingsScore || overallScore || 25
      },
      realTimeMetrics: {
        heartRate: heartRate,
        heartRateContexts: heartRateContexts, // Medical-grade multi-context
        bloodOxygen: bloodOxygen,
        bloodGlucose: bloodGlucose,
        glucoseTrend: glucoseTrend,
        steps: steps,
        calories: calories,
        activeMinutes: activeMinutes,
        distance: parseFloat(distanceKm),
        water: Math.round(waterML / 250),
        waterML: waterML,
        sleep: sleepHours,
        sleepEfficiency: sleepEfficiency,
        deepSleep: deepSleep,
        remSleep: remSleep,
        restingHeartRate: restingHeartRate,
        heartRateVariability: heartRateVariability,
        stress: stressLevel,
        stressLevel: stressLevel,
        stressSource: stressSource,
        temperature: temperature,
        vo2Max: vo2Max,
        respiratoryRate: respiratoryRate,
        bloodPressure: bloodPressure,
        bodyComposition: bodyComposition,
        nervousSystemHealth: nervousSystemHealth,
        glucose: bloodGlucose // Legacy field for compatibility
      },
      medications: medications,
      activePlans: currentActivePlans || [],
      devices: [{
        name: deviceType,
        type: 'multi-device',
        connected: true,
        lastSync: primaryFileDate
      }],
      lastUpdated: new Date().toISOString(),
      dataQuality: {
        percentage: appleWatchScore || ouraReadinessScore || fitbitScore || garminScore || samsungScore || withingsScore || overallScore || 25,
        level: 'Limited',
        availableMetrics: 2,
        totalMetrics: 8
      },
      alerts: [],
      recommendations: steps < 5000 ? [{
        category: 'activity',
        priority: 'high',
        message: 'Increase daily steps for better health',
        target: '10,000 steps/day'
      }] : []
    };
    
    // Set data availability - comprehensive multi-device support with sources
    setDataAvailability({
      heartRate: { available: !!heartRate, source: heartRateSource, contexts: heartRateContexts },
      bloodOxygen: { available: !!bloodOxygen, source: 'Apple Watch' },
      bloodPressure: { available: !!bloodPressure, source: 'Withings' },
      temperature: { available: !!temperature, source: temperatureSource },
      steps: { available: steps > 0, source: activitySource },
      bloodGlucose: { available: !!bloodGlucose, source: null },
      sleep: { available: !!sleepHours, source: sleepSource },
      hydration: { available: waterML > 0, source: deviceWaterML > 0 ? activitySource : 'Manual Entry' },
      stress: { available: stressLevel !== null, source: stressSource, value: stressLevel },
      vo2Max: { available: !!vo2Max, source: activitySource },
      respiratoryRate: { available: !!respiratoryRate, source: null },
      heartRateVariability: { available: !!heartRateVariability, source: sleepSource },
      bodyComposition: { available: !!bodyComposition, source: 'Withings Body Scan' },
      nervousSystemHealth: { available: !!nervousSystemHealth, source: 'Withings Body Scan' },
      restingHeartRate: { available: !!restingHeartRate, source: sleepSource }
    });
    
    return result;
  }, [EMPTY_HEALTH_DATA, profileDataFromLake, activePlansFromLake, getManualWaterIntake]);

  // Fetch health data - following useRealTimeHealthData pattern
  const fetchHealthData = useCallback(async () => {
    console.log(`🚀 INTELLIGENT HOOK: fetchHealthData called with days=${days}`);
    
    if (isFetchingRef.current) {
      console.log('⏸️ Already fetching, skipping...');
      return;
    }

    const userId = user?.localAccountId || userInfo?.localAccountId;
    
    if (!userId) {
      console.log('❌ No user ID available');
      setIsLoading(false);
      return;
    }

    isFetchingRef.current = true;

    try {
      // ⚡ PERFORMANCE OPTIMIZATION: Parallel API calls instead of sequential
      // Calculate date range for IoMT data
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const recordLimit = Math.max(100, days * 10); // At least 100, but scale with days
      
      console.log(`🚀 PARALLEL FETCH: Starting both API calls simultaneously for ${days} days`);
      console.log(`📅 Date range: ${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]} with limit ${recordLimit}`);
      
      // Start both API calls in parallel - should reduce load time from ~30-45s to ~15-20s
      const [profileData, response] = await Promise.all([
        fetchProfileDataFromLake(),
        iomtApiService.getIomtData(userId, {
          limit: recordLimit,
          onlyLatest: false,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        })
      ]);
      
      const currentMedicationsData = profileData?.medicationsData || profileDataFromLake;
      const currentActivePlansData = profileData?.activePlansData || activePlansFromLake;

      if (response?.success && response?.data?.length > 0) {
        // Use enhanced Health Data Integration layer to transform data
        console.log('🔄 HOOK: Using enhanced HealthDataIntegration for comprehensive data mapping');
        const integratedData = HealthDataIntegration.transformForLandingPage(response);
        
        // IMPORTANT: Add raw API response for historical data extraction
        integratedData._rawApiResponse = response;
        console.log(`📊 RAW API: Added raw response with ${response.data?.length || 0} records for historical extraction`);
        
        // Debug medications data
        console.log('💊 MEDICATIONS DEBUG: currentMedicationsData:', currentMedicationsData);
        console.log('💊 MEDICATIONS DEBUG: currentActivePlansData:', currentActivePlansData);
        console.log('💊 MEDICATIONS DEBUG: profileData?.medicationsData:', profileData?.medicationsData);
        console.log('💊 MEDICATIONS DEBUG: profileDataFromLake:', profileDataFromLake);
        console.log('💊 MEDICATIONS DEBUG: Full profileData keys:', profileData ? Object.keys(profileData) : 'null');
        
        // Check if medications might be in a different property
        if (profileData) {
          console.log('💊 LOOKING FOR MEDICATIONS IN:', {
            currentMedications: profileData.currentMedications,
            medications: profileData.medications,
            medicationsData: profileData.medicationsData,
            lifestyle: profileData.lifestyle,
            supplements: profileData.supplements
          });
        }
        
        // Merge with profile data if available
        if (currentMedicationsData || currentActivePlansData) {
          integratedData.medications = currentMedicationsData || [];
          integratedData.activePlans = currentActivePlansData || [];
          console.log('💊 MERGED: medications count:', integratedData.medications?.length || 0);
          console.log('💊 MERGED: activePlans count:', integratedData.activePlans?.length || 0);
        } else {
          console.log('❌ NO MEDICATION DATA: No medications or active plans data to merge');
          // No demo data for production - must use real data only
          integratedData.medications = [];
          integratedData.activePlans = [];
          console.log('� PRODUCTION: No demo medications - using empty arrays for production');
        }
        
        // Add data availability flags
        setDataAvailability(integratedData.dataAvailability);
        
        console.log('✅ HOOK: Enhanced health data integration completed');
        console.log(`📊 METRICS: ${Object.keys(integratedData.realTimeMetrics).length} real-time metrics available`);
        console.log(`📱 DEVICES: ${integratedData.devices.length} connected devices`);
        console.log(`🎯 SCORE: Overall health score ${integratedData.score.overall}%`);
        
        setHealthData(integratedData);
        setLastSync(new Date().toISOString());
        setError(null);
        
        setDeviceStatus({
          connected: true,
          lastSync: new Date().toISOString(),
          deviceCount: integratedData.devices?.length || 0
        });
      } else {
        // Use profile-only data when no health data available
        console.log('⚠️ HOOK: No health data available, using profile-only data');
        const profileOnlyData = transformAzureData({ data: [] }, currentMedicationsData, currentActivePlansData);
        setHealthData(profileOnlyData || EMPTY_HEALTH_DATA);
      }
    } catch (err) {
      console.error('❌ Fetch error:', err);
      setError(err.message);
      setHealthData(EMPTY_HEALTH_DATA);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [user?.localAccountId, userInfo?.localAccountId, fetchProfileDataFromLake, profileDataFromLake, activePlansFromLake, transformAzureData, EMPTY_HEALTH_DATA, days]);

  // Initial fetch
  useEffect(() => {
    const userId = user?.localAccountId || userInfo?.localAccountId;
    
    if (userId && !isFetchingRef.current) {
      console.log(`🎬 INTELLIGENT HOOK: Initial fetch with days=${days}`);
      fetchHealthData();
    }
  }, [user?.localAccountId, userInfo?.localAccountId, fetchHealthData, days]);

  // Manual refresh function
  const refreshData = useCallback(() => {
    console.log(`🔄 INTELLIGENT HOOK: Manual refresh triggered with days=${days}`);
    return fetchHealthData();
  }, [fetchHealthData, days]);

  // DEBUGGING: Expose refresh function to window for manual testing
  useEffect(() => {
    window.forceIntelligentHealthDataRefresh = () => {
      console.log(`🚀 FORCE INTELLIGENT REFRESH: Triggered from console with days=${days}`);
      fetchHealthData();
    };
    return () => {
      delete window.forceIntelligentHealthDataRefresh;
    };
  }, [fetchHealthData, days]);

  // Return stable object reference - matching useRealTimeHealthData interface
  const returnValue = useMemo(() => ({
    healthData,
    isLoading,
    error,
    refreshData,
    deviceStatus,
    lastSync,
    dataAvailability,
    hasAnyData: healthData !== null && healthData.dataQuality?.availableMetrics > 0
  }), [healthData, isLoading, error, refreshData, deviceStatus, lastSync, dataAvailability]);
  
  return returnValue;
};

export default useIntelligentHealthData;
